import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, UserRound } from "lucide-react";
import { API } from "service/Interceptor";
import { formatPersonName, serverFiles } from "utils/utils";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const displayNameOf = (user) => {
  if (user?.userType === "BUSINESS" || user?.companyName) {
    return (
      user?.companyName ||
      formatPersonName(user?.firstName, user?.lastName) ||
      "Brand"
    );
  }
  return (
    formatPersonName(user?.firstName, user?.lastName) ||
    user?.companyName ||
    "User"
  );
};

const initialsOf = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/**
 * Header search — types ahead users and opens their profile on select.
 */
const HeaderUserSearch = ({
  className = "",
  inputClassName = "",
  placeholder = "Search people…",
  autoFocus = false,
  onNavigated,
}) => {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState(null);

  const updateCoords = () => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 260),
    });
  };

  useLayoutEffect(() => {
    if (!open || !query.trim()) {
      setCoords(null);
      return undefined;
    }
    updateCoords();
    const onScrollOrResize = () => updateCoords();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, query]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setOpen(true);

    const timer = window.setTimeout(async () => {
      try {
        // Match existing GlobalSearch path (context-path /api)
        const res = await API.get("/api/search", {
          params: { search: q, page: 0, size: 8 },
        });
        if (cancelled) return;
        const users = res?.data?.data?.userResponse || [];
        setResults(Array.isArray(users) ? users : []);
        setOpen(true);
      } catch (_) {
        if (!cancelled) {
          setResults([]);
          setOpen(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 160);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const goToProfile = (user) => {
    const alias = user?.userAliasId;
    if (!alias) return;
    setOpen(false);
    setQuery("");
    setResults([]);
    onNavigated?.();
    navigate(`/user-profile/${alias}`);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (results[0]) goToProfile(results[0]);
  };

  const showDropdown = open && query.trim().length > 0 && coords;

  const dropdown =
    showDropdown &&
    createPortal(
      <div
        ref={dropdownRef}
        style={{
          position: "fixed",
          top: coords.top,
          left: coords.left,
          width: coords.width,
          zIndex: 200,
        }}
        className="rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/50 overflow-hidden max-h-80 overflow-y-auto"
      >
        {loading && results.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Searching…
          </div>
        ) : results.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-zinc-500">
            No people found
          </div>
        ) : (
          <ul className="py-1.5">
            {results.map((user, idx) => {
              const name = displayNameOf(user);
              const avatar = user?.profileUrl
                ? serverFiles(user.profileUrl)
                : null;
              const isBrand =
                user?.userType === "BUSINESS" || !!user?.companyName;
              return (
                <li key={user?.userAliasId || `${name}-${idx}`}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => goToProfile(user)}
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-zinc-900/80 transition-colors"
                  >
                    {avatar ? (
                      <img
                        src={avatar}
                        alt=""
                        className="h-9 w-9 rounded-full object-cover shrink-0"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = NO_IMAGE;
                        }}
                      />
                    ) : (
                      <div className="avatar-fallback h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">
                        {initialsOf(name)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-zinc-100 truncate">
                        {name}
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate mt-0.5 inline-flex items-center gap-1">
                        <UserRound size={11} />
                        {isBrand ? "Brand" : "Creative"}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>,
      document.body
    );

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <form
        onSubmit={onSubmit}
        className="app-header-search flex items-center gap-2 w-full min-w-0 rounded-full border border-zinc-800 bg-zinc-900/60 px-3.5 h-10 focus-within:border-[#6C63F5] transition-colors"
      >
        <Search size={15} className="text-zinc-500 shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (e.target.value.trim()) setOpen(true);
          }}
          onFocus={() => {
            if (query.trim()) setOpen(true);
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className={`bg-transparent text-sm text-zinc-100 placeholder:text-zinc-500 outline-none w-full min-w-0 ${inputClassName}`}
        />
        {loading && (
          <Loader2 size={14} className="animate-spin text-zinc-500 shrink-0" />
        )}
      </form>
      {dropdown}
    </div>
  );
};

export default HeaderUserSearch;
